import React, { useState, useEffect, useRef, useMemo } from "react";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    FieldWrappers,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
    RupeeIcon,
    CardWrapper,
    SelectionCard,
} from "../../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendar } from "react-icons/fa";
import SelectCategoryModal from "../../Expenses/SelectCategoryModal";
import CategoryCard from "../../CategoryCard";
import SelectItemModal from "../../SelectItemModal";

const AddAutoPayDetails = ({
    addAutoPayMutate,
    addAutoPayLoading,
    handleClose,
    beneficiariesData,
    expCategories,
}) => {
    const categories = useMemo(() => {
        return expCategories?.map((section) => ({
            type: "group",
            name: section.name,
            items: section.items.map((item) => ({
                value: item.id + "",
                label: item.name,
            })),
        }));
    }, [expCategories]);

    const initialValues = {
        beneficiaryId: "",
        expenseType: "",
        totalTransactions: "",
        amount: "",
        totalAmount: "",
        frequencyType: 3,
        // frequencyValue: "",
        nextDueDate: null,
    };

    const [height, setHeight] = useState(window.innerHeight);
    const [sBeneficaryModalOpen, setIsBeneficaryModalOpen] = useState(false);
    const [selectedBeneficary, setSelectedBeneficary] = useState(null);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const formikRef = useRef(null);

    const beneficiary = beneficiariesData;

    const frequencyOptions = [
        { value: 1, label: "Daily" },
        // { value: 2, label: "Weekly" },
        { value: 3, label: "Monthly" },
        // { value: 4, label: "Yearly" },
    ];

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener("resize", handleResize);
        handleResize();

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Calculate amount per transaction
    const calculateAmountPerTransaction = (totalAmount, totalTransactions) => {
        if (totalAmount && totalTransactions && Number(totalTransactions) > 0) {
            return (Number(totalAmount) / Number(totalTransactions)).toFixed(2);
        }
        return null;
    };

    // Yup Validation Schema
    const validationSchema = Yup.object({
        beneficiaryId: Yup.string().required("Please select a beneficiary"),
        expenseType: Yup.string().required("Please select category"),
        totalAmount: Yup.number()
            .required("Total amount is required")
            .positive("Total amount must be greater than 0")
            .max(999999999, "Amount cannot exceed ₹99,99,99,999")


            .typeError("Total amount must be a valid number"),
        totalTransactions: Yup.number()
            .required("Number of transactions is required")
            .positive("Must be greater than 0")
            .integer("Must be a whole number")
            .max(365, "Cannot exceed 365 transactions"),
        frequencyType: Yup.number().required("Please select frequency"),
        // frequencyValue: Yup.number()
        //     .required("Interval is required")
        //     .positive("Must be greater than 0")
        //     .integer("Must be a whole number")
        //     .max(30, "Interval cannot exceed 30"),
        nextDueDate: Yup.date()
            .required("Next due date is required")
            .min(new Date(), "Next due date must be in the future"),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        const amountPerTransaction = calculateAmountPerTransaction(
            values.totalAmount,
            values.totalTransactions
        );

        const payload = {
            beneficiaryId: values.id,
            expenseType: values.expenseType,
            totalTransactions: Number(values.totalTransactions),
            amount: Number(amountPerTransaction),
            totalAmount: Number(values.totalAmount),
            frequencyType: values.frequencyType,
            // frequencyValue: Number(values.frequencyValue),
            nextDueDate: moment(values.nextDueDate).format("YYYY-MM-DD"),
        };

        addAutoPayMutate(payload);
    };

    const handleBeneficiarySelect = (beneficiary) => {
        if (beneficiary && formikRef.current) {
            formikRef.current.setFieldValue("beneficiaryId", beneficiary.cfBeneficiaryId);
            formikRef.current.setFieldValue("id", beneficiary.id || "");
            setSelectedBeneficary(beneficiary);
        } else if (formikRef.current) {
            formikRef.current.setFieldValue("beneficiaryId", "");
            formikRef.current.setFieldValue("id", "");
            setSelectedBeneficary(null);
        }
        setIsBeneficaryModalOpen(false);
    };

    const getSelectedBeneficiaryDisplay = () => {
        if (selectedBeneficary) {
            return {
                label: selectedBeneficary.name,
                value: selectedBeneficary.cfBeneficiaryId,
                subtitle: selectedBeneficary.mobile
            };
        }
        return null;
    };

    const handleSelectedCategory = (selectedOption) => {
        setSelectedCategory(selectedOption);
        if (formikRef.current) {
            formikRef.current.setFieldValue("expenseType", selectedOption.value);
        }
    };

    const handleCategorySave = (category) => {
        handleSelectedCategory(category);
        setShowCategoryModal(false);
    };

    return (
        <Container
            style={{
                height: `${height}px`,
                overflow: "auto",
                WebkitOverflowScrolling: "touch",
            }}
        >
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Setup Autopay</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>
            <div className="custom-scrollbar px-6 h-full flex flex-col justify-between">
                <Formik
                    innerRef={formikRef}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    validateOnChange={true}
                    validateOnBlur={true}
                >
                    {({ values, setFieldValue, errors, touched, isSubmitting }) => {
                        const amountPerTransaction = calculateAmountPerTransaction(
                            values.totalAmount,
                            values.totalTransactions
                        );

                        return (
                            <Form>
                                <ScrollableContent className="max-h-[calc(100vh-140px)]">
                                    <Wrapper>
                                        {/* Select Beneficiary */}
                                        <InputGroup>
                                            <Label htmlFor="beneficiaryId">Select Beneficiary *</Label>
                                            <CategoryCard
                                                title="Beneficiary Name"
                                                selectedCategory={getSelectedBeneficiaryDisplay()}
                                                onClick={() => setIsBeneficaryModalOpen(true)}
                                            />
                                            {errors.beneficiaryId && touched.beneficiaryId && (
                                                <ErrorText>{errors.beneficiaryId}</ErrorText>
                                            )}
                                            {selectedBeneficary && (
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                                        🏦 ••••{selectedBeneficary.accountNum?.slice(-4)}
                                                    </span>
                                                    <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                                                        {selectedBeneficary?.ifsc || '0000'}
                                                    </span>
                                                </div>
                                            )}
                                        </InputGroup>

                                        {/* Category */}
                                        <InputGroup>
                                            <Label htmlFor="expenseType">Expense Categories *</Label>
                                            <CategoryCard
                                                type="button"
                                                selectedCategory={selectedCategory}
                                                title="Category"
                                                onClick={() => setShowCategoryModal(true)}
                                            />
                                            {errors.expenseType && touched.expenseType && (
                                                <ErrorText>{errors.expenseType}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Total Amount */}
                                        <InputGroup>
                                            <Label htmlFor="totalAmount">Total Amount *</Label>
                                            <FieldWrappers>
                                                <RupeeIcon />
                                                <Field
                                                    type="text"
                                                    name="totalAmount"
                                                    id="totalAmount"
                                                    autoComplete="off"
                                                    className={`pl-6 ${errors.totalAmount && touched.totalAmount ? "border-red-500" : ""}`}
                                                    placeholder="Enter total amount"
                                                    min="1"
                                                    step="1"
                                                />
                                            </FieldWrappers>
                                            {errors.totalAmount && touched.totalAmount && (
                                                <ErrorText>{errors.totalAmount}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Number of Transactions */}
                                        <InputGroup>
                                            <Label htmlFor="totalTransactions">Number of Installments *</Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="totalTransactions"
                                                    id="totalTransactions"
                                                    autoComplete="off"
                                                    className={errors.totalTransactions && touched.totalTransactions ? "border-red-500" : ""}
                                                    placeholder="Enter number of transactions"
                                                    min="1"
                                                    max="365"
                                                    step="1"
                                                />
                                            </FieldWrappers>
                                            {errors.totalTransactions && touched.totalTransactions && (
                                                <ErrorText>{errors.totalTransactions}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Amount Per Transaction Display */}
                                        {amountPerTransaction && (
                                            <div className="bg-primary-color/10 border border-primary-color/30 rounded-lg px-4 py-2">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-medium text-gray-700">Installment Amount:</span>
                                                    <span className="text-base font-bold text-primary-color">₹{amountPerTransaction}</span>
                                                </div>
                                            </div>
                                        )}

                                        {/* Frequency Type - Chips */}
                                        <InputGroup>
                                            <Label>Frequency *</Label>
                                            <CardWrapper>
                                                {frequencyOptions.map((freq) => (
                                                    <SelectionCard
                                                        key={freq.value}
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            setFieldValue("frequencyType", freq.value);
                                                        }}
                                                        className={
                                                            values.frequencyType === freq.value
                                                                ? "bg-[#0078BD] text-white"
                                                                : "bg-gray-100 text-gray-800"
                                                        }
                                                    >
                                                        <span className="text-sm">{freq.label}</span>
                                                    </SelectionCard>
                                                ))}
                                            </CardWrapper>
                                            {errors.frequencyType && touched.frequencyType && (
                                                <ErrorText>{errors.frequencyType}</ErrorText>
                                            )}
                                        </InputGroup>

                                        {/* Next Due Date */}
                                        <InputGroup>
                                            <Label htmlFor="nextDueDate">First Payment Date *</Label>
                                            <FieldWrappers className="relative w-full">
                                                <DatePicker
                                                    selected={values.nextDueDate ? moment(values.nextDueDate, "YYYY-MM-DD").toDate() : null}
                                                    onChange={(date) =>
                                                        setFieldValue("nextDueDate", moment(date).format("YYYY-MM-DD"))
                                                    }
                                                    minDate={moment().add(1, 'days').toDate()}
                                                    dateFormat="yyyy-MM-dd"
                                                    className="w-full cursor-pointer"
                                                    placeholderText="YYYY-MM-DD"
                                                    onClickOutside={() => setCalendarOpen(false)}
                                                    onSelect={() => setCalendarOpen(false)}
                                                    open={calendarOpen}
                                                    onInputClick={() => setCalendarOpen(true)}
                                                    popperPlacement="top-end"
                                                    showIcon
                                                    icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                    showPopperArrow={false}
                                                    onFocus={(e) => e.target.blur()}
                                                    wrapperClassName="w-full"
                                                    portalId="root-portal"
                                                    popperClassName="react-datepicker-popper"
                                                />
                                            </FieldWrappers>
                                            {errors.nextDueDate && touched.nextDueDate && (
                                                <ErrorText>{errors.nextDueDate}</ErrorText>
                                            )}
                                        </InputGroup>
                                        {/* Interval Input */}
                                        {/* <InputGroup>
                                            <Label htmlFor="frequencyValue">
                                                Interval (in {values.frequencyType === 1 ? 'days' :
                                                    values.frequencyType === 2 ? 'weeks' :
                                                        values.frequencyType === 3 ? 'months' :
                                                            values.frequencyType === 4 ? 'years' : ''}) *
                                            </Label>
                                            <FieldWrappers>
                                                <Field
                                                    type="text"
                                                    name="frequencyValue"
                                                    id="frequencyValue"
                                                    autoComplete="off"
                                                    className={errors.frequencyValue && touched.frequencyValue ? "border-red-500" : ""}
                                                    placeholder="Enter interval"
                                                    min="1"
                                                    step="1"
                                                />
                                            </FieldWrappers>
                                            {errors.frequencyValue && touched.frequencyValue && (
                                                <ErrorText>{errors.frequencyValue}</ErrorText>
                                            )}
                                        </InputGroup> */}

                                    </Wrapper>
                                </ScrollableContent>

                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={handleClose}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={isSubmitting || addAutoPayLoading}>
                                        {isSubmitting || addAutoPayLoading ? "Submitting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                                {/* Beneficiary Selection Modal */}
                                <SelectItemModal
                                    isOpen={sBeneficaryModalOpen}
                                    onClose={() => setIsBeneficaryModalOpen(false)}
                                    onSelect={handleBeneficiarySelect}
                                    options={beneficiary || []}
                                    selectedItem={selectedBeneficary}
                                    title="Select Beneficiary"
                                    searchPlaceholder="Search beneficiary..."
                                    idKey="id"
                                    nameKey="name"
                                    descriptionKeys={["mobile"]}
                                    emptyMessage="No beneficiary available"
                                    noResultsMessage="No beneficiary found"
                                />

                                {/* Category Selection Modal */}
                                <SelectCategoryModal
                                    isOpen={showCategoryModal}
                                    onClose={() => setShowCategoryModal(false)}
                                    onSelect={handleCategorySave}
                                    categories={categories}
                                    selectedCategory={selectedCategory}
                                />
                            </Form>
                        );
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const ErrorText = tw.div`text-red-500 text-sm ml-0.5`;

export default AddAutoPayDetails;